/**
 * @file journey.schema.ts
 * @description Define o schema Zod para a entidade 'Journey'.
 * Uma Journey é um plano mestre, um template de atividades e cronogramas
 * que define a sequência de cuidados recomendada durante uma gestação.
 * Pode ser a jornada padrão do app ou uma customizada por um parceiro.
 */

import { z } from 'zod';

import { RecurrenceRuleSchema } from './recurrence-rule.schema';

// --- Helper Schemas & Enums ---

/**
 * Unidade de tempo para durações e antecedências.
 * Como é uma categoria fixa, usamos UPPER_SNAKE_CASE.
 */
const TimeUnitEnum = z.enum(['DAYS', 'WEEKS', 'MONTHS']);

/**
 * Define um período de tempo com valor e unidade. Usado para alertas.
 * Ex: { value: 2, unit: 'WEEKS' } -> 2 semanas.
 */
const LeadTimeSchema = z.object({
  value: z.number().int().positive(),
  unit: TimeUnitEnum,
});

/**
 * Define um intervalo de idade gestacional, em dias ou semanas.
 */
const GestationalAgeRangeSchema = z.object({
  start: z.number().int(),
  end: z.number().int(),
  unit: z.enum(['days', 'weeks']),
});

/**
 * Define uma única etapa (step) dentro de uma jornada. Cada etapa corresponde
 * a uma atividade que será criada para a gestante.
 */
const JourneyStepSchema = z.object({
  /** Título customizado para esta etapa da jornada. Ex: "Ultrassom Morfológico do 2º Trimestre". */
  title: z.string(),
  
  /** Descrição opcional que explica a importância ou o contexto desta etapa. */
  description: z.string().nullable(),

  /** O ID do 'ActivityTemplate' que esta etapa irá instanciar. */
  templateId: z.string(),
  
  /** O intervalo ideal de idade gestacional para esta atividade acontecer. */
  idealGestationalAgeRange: GestationalAgeRangeSchema,
  
  /** Com quanto tempo de antecedência um alerta normal deve ser disparado. */
  normalAlertLeadTime: LeadTimeSchema,
  
  /** Opcional: antecedência para um alerta crítico, se a data estiver se aproximando. */
  criticalAlertLeadTime: LeadTimeSchema.nullable(),

  /** Opcional: regra de recorrência para esta atividade, se aplicável. */
  recurrence: RecurrenceRuleSchema.nullable(),
});


// --- Main Journey Schema ---

const JourneySchema = z.object({
  /**
   * O nome da jornada, para identificação no painel de administração.
   * Ex: "Jornada Padrão", "Programa Bem-Viver da Empresa X".
   */
  name: z.string(),
  
  /**
   * Descrição opcional sobre o foco ou objetivo principal desta jornada.
   */
  description: z.string().nullable(),

  /**
   * O ID do parceiro ('Partner') ao qual esta jornada pertence.
   * Se o valor for nulo, esta é a jornada padrão do aplicativo,
   * aplicada a todos os usuários B2C que não têm um parceiro.
   */
  partnerId: z.string().nullable(),

  /**
   * A lista ordenada de etapas (passos) que compõem a jornada.
   * Esta é a "receita" que a Cloud Function usará para criar as
   * instâncias de 'Activity' para cada nova gestação.
   */
  steps: z.array(JourneyStepSchema),

  /**
   * Campos de auditoria para rastrear a criação e modificação da jornada.
   */
  createdAt: z.date(),
  updatedAt: z.date(),
  deletedAt: z.date().nullable(),
});


// --- Inferred TypeScript Types ---

type Journey = z.infer<typeof JourneySchema>;
type JourneyStep = z.infer<typeof JourneyStepSchema>;
type LeadTime = z.infer<typeof LeadTimeSchema>;
type GestationalAgeRange = z.infer<typeof GestationalAgeRangeSchema>;
type TimeUnit = z.infer<typeof TimeUnitEnum>;


// --- Public Exports ---

export {
  JourneySchema,
  JourneyStepSchema,
  LeadTimeSchema,
  GestationalAgeRangeSchema,
  TimeUnitEnum,
};

export type {
  Journey,
  JourneyStep,
  LeadTime,
  GestationalAgeRange,
  TimeUnit,
};